import React, { useState, useEffect } from 'react';
import { Megaphone, AlertCircle, Calendar } from 'lucide-react';
import { getAnnouncements } from '../../../services/restaurantApi';
import { PageSkeleton } from '../DashboardLoader';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const RestaurantAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('active');
  const [sortBy, setSortBy] = useState('date_desc');

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getAnnouncements();
        setAnnouncements(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erreur lors du chargement des annonces:', err);
        setError('Impossible de charger les annonces.');
        setAnnouncements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getExpiryLabel = (expiresAt) => {
    if (!expiresAt) return 'Sans expiration';
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (!Number.isFinite(ms)) return 'Expiration invalide';
    if (ms <= 0) return 'Expirée';
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    if (days <= 1) return 'Expire aujourd hui';
    return `Expire dans ${days} jours`;
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    const t = new Date(expiresAt).getTime();
    if (!Number.isFinite(t)) return false;
    return t <= Date.now();
  };

  const activeCount = announcements.filter((ann) => !isExpired(ann.expires_at)).length;
  const archivedCount = announcements.filter((ann) => isExpired(ann.expires_at)).length;
  const allCount = announcements.length;

  const visibleAnnouncements = announcements.filter((ann) => {
    if (view === 'active') return !isExpired(ann.expires_at);
    if (view === 'archived') return isExpired(ann.expires_at);
    return true;
  }).sort((a, b) => {
    const toTime = (v) => {
      const t = new Date(v || 0).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    const urgency = (ann) => (ann?.is_urgent ? 2 : ((ann?.priority || '').toLowerCase() === 'high' ? 1 : 0));
    if (sortBy === 'date_asc') return toTime(a.created_at) - toTime(b.created_at);
    if (sortBy === 'urgent_first') return urgency(b) - urgency(a) || (toTime(b.created_at) - toTime(a.created_at));
    return toTime(b.created_at) - toTime(a.created_at);
  });

  if (loading) return <PageSkeleton />;

  const VIEWS = [
    { id: 'active', label: 'Actives', count: activeCount },
    { id: 'archived', label: 'Archives', count: archivedCount },
    { id: 'all', label: 'Toutes', count: allCount },
  ];

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* Header */}
      <div className="flex items-end gap-4 flex-wrap">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            {allCount} annonce{allCount > 1 ? 's' : ''}
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Annonces</h1>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '4px' }}>
          {VIEWS.map((v) => {
            const active = view === v.id;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                style={{
                  border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px',
                  padding: '9px 18px', borderRadius: '999px', whiteSpace: 'nowrap',
                  background: active ? PRIMARY : 'transparent', color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                }}
              >
                {v.label} · {v.count}
              </button>
            );
          })}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="sm:ml-auto"
          style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '44px', fontSize: '13.5px', color: '#051004', outline: 'none' }}
        >
          <option value="date_desc">Plus récentes</option>
          <option value="date_asc">Plus anciennes</option>
          <option value="urgent_first">Urgentes d'abord</option>
        </select>
      </div>

      {/* Liste */}
      {visibleAnnouncements.length === 0 ? (
        <div style={{ background: '#FCFBF5', borderRadius: '30px', padding: '56px 24px', textAlign: 'center' }}>
          <Megaphone size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
          <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>Aucune annonce ici</h3>
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>Rien à afficher pour cette vue.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {visibleAnnouncements.map((ann) => (
            <div
              key={ann.id}
              style={{
                background: ann.is_urgent ? '#FEF0EA' : '#FCFBF5',
                border: ann.is_urgent ? '1px solid rgba(163,44,15,0.18)' : 'none',
                borderRadius: '26px',
                padding: '20px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div className="flex items-center gap-2.5 flex-wrap">
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '18px' }}>{ann.title}</strong>
                {ann.is_urgent && (
                  <span style={{ background: TERRA, color: '#FCFBF5', fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '5px 12px', borderRadius: '999px' }}>
                    Urgent
                  </span>
                )}
              </div>
              <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.55, color: 'rgba(5,16,4,0.78)', whiteSpace: 'pre-wrap' }}>
                {ann.content}
              </p>
              <div className="flex items-center gap-4 flex-wrap" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.5)' }}>
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} />
                  {formatDate(ann.created_at)}
                </span>
                {ann.expires_at && <span>Expire le {formatDate(ann.expires_at)}</span>}
                <span style={{ fontWeight: 600, color: isExpired(ann.expires_at) ? 'rgba(5,16,4,0.5)' : '#A32C0F' }}>
                  {getExpiryLabel(ann.expires_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RestaurantAnnouncements;
